import { MouseEvent } from "react";

import { CameraIcon, CheckIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { createColumnHelper } from "@tanstack/react-table";
import { DateTime } from "luxon";

import { IPart } from "../../models/Part";
import { IListingOptimisation } from "../../models/listingOptimisation/IListingOptimisation";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import { ListingAgeTone, daysSince, listingAgeTone } from "../../utils/listingAge";
import { NextStepTone } from "../../utils/listingNextStep";
import { STOCK_HEAVY_THRESHOLD } from "../../utils/listingNextStep";
import { PriceDeltaTone } from "../../utils/listingPriceDelta";
import PriceFormat from "../../utils/priceFormat";
import { MISSING_FIELD_OPTIONS } from "../ebayListings/listedTable/missingFields";
import PartGradeBadge from "../shared/PartGradeBadge";
import { IndeterminateCheckbox } from "../shared/Tables/IndeterminateCheckbox";
import { IQueueRowSignals, computeQueueRowSignals } from "./queueRowSignals";

const colHelper = createColumnHelper<IPart>();

// Row objects are stable between fetches, so the derived signals are computed once per row and
// shared by every cell and the sort accessors.
const signalsCache = new WeakMap<IPart, IQueueRowSignals>();

export function signalsFor(part: IPart): IQueueRowSignals {
  let signals = signalsCache.get(part);
  if (!signals) {
    signals = computeQueueRowSignals(part);
    signalsCache.set(part, signals);
  }
  return signals;
}

export interface IQueueColumnOptions {
  selectedIds: Set<string>;
  pageRows: IPart[];
  onToggleRow: (partId: string) => void;
  onToggleAll: () => void;
  optimisationsByPartId: Map<string, IListingOptimisation>;
  findUserName: (userId: string | null | undefined) => string | null;
  onOpenPhotos: (part: IPart) => void;
  onMarkReviewed: (part: IPart) => void;
  markingReviewedIds: Set<string>;
}

const AGE_TONE_CLASSES: Record<ListingAgeTone, string> = {
  fresh: "bg-green-100 text-green-700",
  stale: "bg-amber-100 text-amber-700",
  old: "bg-orange-100 text-orange-700",
  longTail: "bg-red-100 text-red-700",
};

const DELTA_TONE_CLASSES: Record<PriceDeltaTone, string> = {
  above: "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200",
  below: "bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200",
  within: "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200",
  noSold: "bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-200",
  none: "bg-gray-50 text-gray-500 ring-1 ring-inset ring-gray-200",
};

export const NEXT_STEP_TONE_CLASSES: Record<NextStepTone, string> = {
  blocked: "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200",
  attention: "bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200",
  go: "bg-green-50 text-green-700 ring-1 ring-inset ring-green-200",
  stock: "bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200",
  neutral: "bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-200",
};

export const scoreToneClasses = (percent: number): string =>
  percent >= 80 ? "bg-green-100 text-green-700" : percent >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";

const missingLabel = (key: string): string => MISSING_FIELD_OPTIONS.find((option) => option.key === key)?.label ?? key;

export function vehicleLine(part: IPart): string {
  const vehicle = part.vehicle;
  if (!vehicle) return "";
  const year = vehicle.dvlaYearOfManufacture ?? vehicle.modelYear;
  return [year, vehicle.combinedMake, vehicle.combinedModel, vehicle.combinedFuelType]
    .filter((value) => value != null && String(value).trim().length > 0)
    .join(" ");
}

export function thumbnailUrlOf(part: IPart): string | null {
  const first = Array.isArray(part.images) ? part.images.find((image) => !image.deleted && (image.thumbnailUrl || image.url)) : undefined;
  return first?.thumbnailUrl || first?.url || part.cdnThumbnail || part.ebayThumbnail || null;
}

const stop = (e: MouseEvent) => e.stopPropagation();

/** What the staged record changes, in a couple of words, for the row captions. */
export function describeStaged(optimisation: IListingOptimisation | undefined): string | null {
  if (!optimisation) return null;
  const parts: string[] = [];
  if (optimisation.newTitle != null) parts.push("title");
  if (optimisation.newPrice != null) parts.push("price");
  if (optimisation.newFulfillmentPolicyId || optimisation.newPaymentPolicyId || optimisation.newReturnPolicyId || optimisation.newLocationKey) {
    parts.push("policies");
  }
  if (optimisation.applyImageRules || optimisation.removeBackgroundFirstImage) parts.push("images");
  if (optimisation.applyCategoryDefaults) parts.push("defaults");
  if (parts.length === 0) return "changes staged";
  return `${parts.join(", ")} staged`;
}

function firstListedCaption(part: IPart, signals: IQueueRowSignals, optimisation: IListingOptimisation | undefined): string {
  const bits: string[] = [];
  if (signals.listingDays != null) bits.push(`This listing ${signals.listingDays}d`);

  const staged = describeStaged(optimisation);
  const rule = part.relistingRule;

  if (signals.relistBlocked) bits.push("relist blocked");
  else if (staged) bits.push(staged);
  else if (rule && signals.listingDays != null && rule.reductionInterval > 0) {
    const due = rule.reductionInterval - signals.listingDays;
    bits.push(due > 0 ? `auto-relist due in ${due}d` : "auto-relist due");
  } else if (signals.relistCount > 0) bits.push(`no sale in ${signals.relistCount} relist${signals.relistCount === 1 ? "" : "s"}`);
  else if (!rule) bits.push(signals.relistCount === 0 ? "Never relisted · no rule on category" : "no rule on category");

  return bits.join(" · ");
}

function ThumbnailCell({ part, onOpenPhotos }: { part: IPart; onOpenPhotos: (part: IPart) => void }) {
  const url = thumbnailUrlOf(part);
  const count = signalsFor(part).imageCount;

  return (
    <div className="flex flex-col items-center gap-1" onClick={stop}>
      <button
        type="button"
        onClick={() => onOpenPhotos(part)}
        title="Manage photos"
        aria-label="Manage photos"
        className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50 transition hover:border-primary-300 hover:ring-2 hover:ring-primary-200"
      >
        {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <CameraIcon className="h-7 w-7 text-gray-400" aria-hidden="true" />}
      </button>
      <span className="text-[11px] text-gray-500">{count == null ? "—" : `${count} photo${count === 1 ? "" : "s"}`}</span>
    </div>
  );
}

function PartCell({ part }: { part: IPart }) {
  const name = part.category?.name || part.title || "Part";
  const vehicle = vehicleLine(part);
  const plate = part.vehicle?.vrm;

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="truncate text-sm font-semibold text-gray-900" title={part.offers?.[0]?.title ?? name}>
        {name}
      </span>
      {vehicle && <span className="truncate text-xs text-gray-600">{vehicle}</span>}
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-600" onClick={stop}>
        {plate && (
          <span className="rounded bg-slate-900 px-1.5 py-0.5 font-mono text-[11px] font-semibold tracking-wider text-amber-300">
            {plate}
          </span>
        )}
        {part.tagNumber && <span>{part.tagNumber}</span>}
        <PartGradeBadge part={part} />
        {part.warehouseLocation?.displayName && <span className="text-gray-500">{part.warehouseLocation.displayName}</span>}
      </div>
    </div>
  );
}

function FirstListedCell({ part, optimisation }: { part: IPart; optimisation: IListingOptimisation | undefined }) {
  const signals = signalsFor(part);
  const tone = listingAgeTone(signals.firstListedDays);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <span className={classNames("rounded-full px-2 py-0.5 text-sm font-bold tabular-nums", tone ? AGE_TONE_CLASSES[tone] : "bg-gray-100 text-gray-500")}>
          {signals.firstListedDays != null ? `${signals.firstListedDays}d` : "—"}
        </span>
        <span
          className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] text-slate-600"
          title={`Relisted ${signals.relistCount} time${signals.relistCount === 1 ? "" : "s"}`}
        >
          r{signals.relistCount}
        </span>
      </div>
      <span className="text-[11px] leading-4 text-gray-500">{firstListedCaption(part, signals, optimisation)}</span>
    </div>
  );
}

function PriceVsMarketCell({ part }: { part: IPart }) {
  const signals = signalsFor(part);
  const intelligenceCount = part.intelligenceKeptCount ?? part.intelligenceFoundCount ?? part.intelligenceActiveCount ?? null;
  const sold = signals.soldSummary;
  const windowDays = sold?.windowDays ?? part.marketInsights?.soldByVehicle?.windowDays ?? 90;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold tabular-nums text-gray-900">{signals.livePrice != null ? PriceFormat(signals.livePrice) : "—"}</span>
        <span className={classNames("rounded-full px-2 py-0.5 text-[11px] font-semibold", DELTA_TONE_CLASSES[signals.delta.tone])}>
          {signals.delta.label}
        </span>
      </div>
      <span className="text-[11px] leading-4 text-amber-800">
        Intelligence {part.intelligenceAverage != null ? PriceFormat(part.intelligenceAverage) : "—"}
        {intelligenceCount != null ? ` · ${intelligenceCount}` : ""}
      </span>
      <span className="text-[11px] leading-4 text-emerald-800">
        Sold {sold?.average != null ? PriceFormat(sold.average) : "—"} · {sold?.count ?? 0} in {windowDays}d
      </span>
    </div>
  );
}

function InStockCell({ part }: { part: IPart }) {
  const signals = signalsFor(part);
  const heavy = signals.stockByVehicle >= STOCK_HEAVY_THRESHOLD;

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5">
        <span className={classNames("text-lg font-semibold tabular-nums", heavy ? "text-sky-700" : "text-gray-900")}>{signals.stockByVehicle}</span>
        <span className="text-[11px] text-gray-500">same car</span>
        {heavy && <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-800">Heavy</span>}
      </div>
      <span className="text-[11px] text-gray-500">{signals.stockByPartNumber} same MPN</span>
    </div>
  );
}

function HealthCell({ part, optimisation }: { part: IPart; optimisation: IListingOptimisation | undefined }) {
  const signals = signalsFor(part);
  const { score } = signals;

  let line: { text: string; tone: string };
  if (signals.relistBlocked) {
    line = { text: `Can't relist: ${signals.relistErrors.join("; ")}`, tone: "text-red-700" };
  } else if (score.missing.length > 0) {
    line = { text: `Missing ${score.missing.map(missingLabel).join(", ")}`, tone: "text-amber-800" };
  } else if (optimisation?.newTitle != null) {
    line = { text: `New title staged · ${optimisation.newTitle.length}/80`, tone: "text-sky-700" };
  } else {
    line = { text: "Nothing missing", tone: "text-gray-500" };
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline gap-1.5">
        <span className={classNames("rounded-full px-2 py-0.5 text-sm font-bold tabular-nums", scoreToneClasses(score.percent))}>{score.percent}%</span>
        <span className="text-[11px] text-gray-500">
          {score.present} of {score.total}
        </span>
      </div>
      <span className={classNames("line-clamp-2 text-[11px] leading-4", line.tone)} title={line.text}>
        {line.text}
      </span>
    </div>
  );
}

function ReviewedCell({
  part,
  findUserName,
  onMarkReviewed,
  marking,
}: {
  part: IPart;
  findUserName: IQueueColumnOptions["findUserName"];
  onMarkReviewed: (part: IPart) => void;
  marking: boolean;
}) {
  const reviewedOn = part.listingReviewedOn ?? null;
  const days = daysSince(reviewedOn);
  const by = findUserName(part.listingReviewedBy);

  if (!reviewedOn) {
    return (
      <div className="flex flex-col items-start gap-1" onClick={stop}>
        <span className="text-sm text-gray-500">Never</span>
        <button
          type="button"
          onClick={() => onMarkReviewed(part)}
          disabled={marking}
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
        >
          <CheckIcon className="h-3 w-3" aria-hidden="true" />
          Mark
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-sm text-gray-900">{formatDateTime(reviewedOn, { day: "numeric", month: "short" })}</span>
      <span className="text-[11px] text-gray-500">
        {days != null ? `${days}d ago` : ""}
        {by ? ` · ${by}` : ""}
      </span>
    </div>
  );
}

function NextStepCell({ part }: { part: IPart }) {
  const { nextStep } = signalsFor(part);
  return (
    <div className="flex items-center justify-between gap-2">
      <span className={classNames("rounded-full px-2.5 py-1 text-xs font-semibold", NEXT_STEP_TONE_CLASSES[nextStep.tone])}>{nextStep.label}</span>
      <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
    </div>
  );
}

const th = (extra: string) => `${extra} !px-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500`;
const td = (extra: string) => `${extra} !px-2 align-top`;

/**
 * The queue's columns. Selection is owned by the queue (a set of part ids), not by TanStack, so
 * the bulk bar can read it; the sort accessors let the page sort by the derived signals while
 * meta.sortKey tells the server which column to order the page by.
 */
export function buildQueueColumns(options: IQueueColumnOptions) {
  const { selectedIds, pageRows, onToggleRow, onToggleAll, optimisationsByPartId, findUserName, onOpenPhotos, onMarkReviewed, markingReviewedIds } = options;

  const allSelected = pageRows.length > 0 && pageRows.every((row) => selectedIds.has(row.id));
  const someSelected = !allSelected && pageRows.some((row) => selectedIds.has(row.id));

  return [
    colHelper.display({
      id: "select",
      enableSorting: false,
      meta: { thClassName: "!px-2 w-10", tdClassName: "!px-2 w-10 align-top" },
      header: () => (
        <div className="flex items-center justify-center" onClick={stop}>
          <IndeterminateCheckbox className="!p-2" checked={allSelected} indeterminate={someSelected} onChange={onToggleAll} aria-label="Select all on this page" />
        </div>
      ),
      cell: (info) => (
        <div className="flex items-center justify-center pt-1" onClick={stop}>
          <IndeterminateCheckbox
            className="!p-2"
            checked={selectedIds.has(info.row.original.id)}
            onChange={() => onToggleRow(info.row.original.id)}
            aria-label="Select listing"
          />
        </div>
      ),
    }),
    colHelper.display({
      id: "thumbnail",
      enableSorting: false,
      meta: { thClassName: "!px-2 w-20", tdClassName: "!px-2 w-20 align-top" },
      cell: (info) => <ThumbnailCell part={info.row.original} onOpenPhotos={onOpenPhotos} />,
    }),
    colHelper.display({
      id: "part",
      header: "Part",
      enableSorting: false,
      meta: { thClassName: th("min-w-[18rem]"), tdClassName: td("min-w-[18rem] max-w-[24rem]") },
      cell: (info) => <PartCell part={info.row.original} />,
    }),
    colHelper.accessor((row) => signalsFor(row).firstListedDays ?? -1, {
      id: "firstListed",
      header: "First listed",
      enableSorting: true,
      sortingFn: "basic",
      meta: { sortKey: "firstListed", thClassName: th("min-w-[11rem]"), tdClassName: td("min-w-[11rem]") },
      cell: (info) => <FirstListedCell part={info.row.original} optimisation={optimisationsByPartId.get(info.row.original.id)} />,
    }),
    colHelper.accessor((row) => Math.abs(signalsFor(row).delta.fraction ?? -1), {
      id: "priceGap",
      header: "Price vs market",
      enableSorting: true,
      sortingFn: "basic",
      meta: { sortKey: "firstListed", thClassName: th("min-w-[12rem]"), tdClassName: td("min-w-[12rem]") },
      cell: (info) => <PriceVsMarketCell part={info.row.original} />,
    }),
    colHelper.accessor((row) => signalsFor(row).stockByVehicle, {
      id: "stock",
      header: "In stock",
      enableSorting: true,
      sortingFn: "basic",
      meta: { sortKey: "firstListed", thClassName: th("min-w-[7rem]"), tdClassName: td("min-w-[7rem]") },
      cell: (info) => <InStockCell part={info.row.original} />,
    }),
    colHelper.accessor((row) => signalsFor(row).score.percent, {
      id: "health",
      header: "Listing health",
      enableSorting: true,
      sortingFn: "basic",
      meta: { sortKey: "firstListed", thClassName: th("min-w-[11rem]"), tdClassName: td("min-w-[11rem] max-w-[14rem]") },
      cell: (info) => <HealthCell part={info.row.original} optimisation={optimisationsByPartId.get(info.row.original.id)} />,
    }),
    colHelper.accessor((row) => (row.listingReviewedOn ? DateTime.fromISO(row.listingReviewedOn, { zone: "utc" }).toMillis() : 0), {
      id: "listingReviewed",
      header: "Reviewed",
      enableSorting: true,
      sortingFn: "basic",
      meta: { sortKey: "listingReviewed", thClassName: th("min-w-[7rem]"), tdClassName: td("min-w-[7rem]") },
      cell: (info) => (
        <ReviewedCell
          part={info.row.original}
          findUserName={findUserName}
          onMarkReviewed={onMarkReviewed}
          marking={markingReviewedIds.has(info.row.original.id)}
        />
      ),
    }),
    colHelper.display({
      id: "nextStep",
      header: "Next step",
      enableSorting: false,
      meta: { thClassName: th("min-w-[9rem]"), tdClassName: td("min-w-[9rem]") },
      cell: (info) => <NextStepCell part={info.row.original} />,
    }),
  ];
}
